import { DestroyRef, Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  EMPTY,
  Observable,
  Subscription,
  catchError,
  distinctUntilChanged,
  finalize,
  switchMap,
  take
} from 'rxjs';
import { ApiErrorCodes } from '../../../../core/enums/ErrorCodes';
import { NotificationService } from '../../../../core/services/NotificationService';
import { IOtsStaffOrder } from '../interfaces/IOtsStaffOrder';
import { OtsStaffOrdersStore } from '../stores/OtsStaffOrdersStore';
import { OtsStaffKitchenService } from './OtsStaffKitchenService';

@Injectable({ providedIn: 'root' })
export class OtsStaffOrdersService {

  private readonly _otsStaffOrdersStore = inject(OtsStaffOrdersStore);
  private readonly _otsStaffKitchenService = inject(OtsStaffKitchenService);
  private readonly _notificationService = inject(NotificationService);
  private readonly _destroyRef = inject(DestroyRef);

  private _watchSub: Subscription | null = null;

  public watchCinema(cinemaId$: Observable<number | null>, destroyRef: DestroyRef): void {
    this._watchSub?.unsubscribe();
    this._watchSub = cinemaId$.pipe(
      distinctUntilChanged(),
      switchMap(cinemaId => this.openStream(cinemaId)),
      takeUntilDestroyed(destroyRef)
    ).subscribe(orders => {
      this._otsStaffOrdersStore.orders.set(orders);
      this._otsStaffOrdersStore.loadError.set(null);
    });
  }

  public reset(): void {
    this._watchSub?.unsubscribe();
    this._watchSub = null;
    this._otsStaffOrdersStore.orders.set([]);
    this._otsStaffOrdersStore.cinemaId.set(null);
    this._otsStaffOrdersStore.loadError.set(null);
    this._otsStaffOrdersStore.movingOrderIds.set(new Set());
    this._otsStaffOrdersStore.searchQuery.set('');
  }

  public moveToNextStatus(orderId: string): void {
    const cinemaId = this._otsStaffOrdersStore.cinemaId();
    const order = this._otsStaffOrdersStore.orders().find(item => item.id === orderId);

    if (cinemaId == null || cinemaId <= 0 || !order || this._otsStaffOrdersStore.movingOrderIds().has(orderId)) {
      return;
    }

    this.setOrderMoving(orderId, true);

    this._otsStaffKitchenService.moveKitchenOrderToNextStatus(orderId, cinemaId, order.apiStatus).pipe(
      take(1),
      finalize(() => this.setOrderMoving(orderId, false)),
      catchError(error => {
        this._notificationService.pushError(
          'Error'.trans('notifications/error.Title'),
          this.moveErrorMessage(error)
        );

        return EMPTY;
      }),
      takeUntilDestroyed(this._destroyRef)
    ).subscribe(movedOrder => {
      if (this._otsStaffOrdersStore.cinemaId() !== cinemaId) {
        return;
      }

      this._otsStaffOrdersStore.orders.update(orders => this.replaceOrder(orders, movedOrder));
    });
  }

  public setSearchQuery(query: string): void {
    this._otsStaffOrdersStore.searchQuery.set(query);
  }

  private openStream(cinemaId: number | null): Observable<IOtsStaffOrder[]> {
    this._otsStaffOrdersStore.cinemaId.set(cinemaId);
    this._otsStaffOrdersStore.loadError.set(null);
    this._otsStaffOrdersStore.orders.set([]);

    if (cinemaId == null || cinemaId <= 0) {
      return EMPTY;
    }

    return this._otsStaffKitchenService.watchOrders(
      cinemaId,
      message => this._otsStaffOrdersStore.loadError.set(message)
    ).pipe(
      catchError(error => {
        this._otsStaffOrdersStore.loadError.set(this.errorMessage(error));

        return EMPTY;
      })
    );
  }

  private setOrderMoving(orderId: string, isMoving: boolean): void {
    this._otsStaffOrdersStore.movingOrderIds.update(movingOrderIds => {
      const nextMovingOrderIds = new Set(movingOrderIds);

      if (isMoving) {
        nextMovingOrderIds.add(orderId);
      } else {
        nextMovingOrderIds.delete(orderId);
      }

      return nextMovingOrderIds;
    });
  }

  private replaceOrder(orders: IOtsStaffOrder[], order: IOtsStaffOrder): IOtsStaffOrder[] {
    if (orders.some(item => item.id === order.id)) {
      return orders.map(item => item.id === order.id ? order : item);
    }

    return [...orders, order];
  }

  private moveErrorMessage(error: unknown): string {
    const errorCode = error instanceof HttpErrorResponse ? error.error?.message : null;

    if (errorCode === ApiErrorCodes.GuestOrderingOrderStatusChanged) {
      return 'The order status has changed. The board shows the current status.'.trans(
        `errors/notifications/${ApiErrorCodes.GuestOrderingOrderStatusChanged}.Message`
      );
    }

    return this.errorMessage(error);
  }

  private errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : 'OtsRequestFailed';
  }
}
